import map from 'lodash/map'
import isEmpty from 'lodash/isEmpty'
import forEach from 'lodash/forEach'
import filter from 'lodash/filter'
import compact from 'lodash/compact'
import toPairs from 'lodash/toPairs'

import { jsonTryParse } from '../json'
import { toDate } from '../conversion'
import type { DurationInputArg1, DurationInputArg2 } from 'moment'

export function localFilter({ searchValue, searchFields, items }) {
  return filter(items, (item) => {
    if (!searchValue) return true

    let result = false

    forEach(searchFields, (searchField) => {
      const match = item[`${searchField.name}`] && item[`${searchField.name}`].toString().match(new RegExp(searchValue, 'i'))

      if (match) {
        result = true
      }
    })
    return result
  })
}

export function replaceYear({ replaceValues, filter }) {
  const argsReplaceDictionary = (text, name, value) => {
    text = JSON.stringify(text)
    //if the value is string, it should be wrapped by quote
    if (typeof value === 'string') {
      value = `"${value}"`
    }
    //if the value is object, convert it to json.stringify
    else if (typeof value === 'object') value = JSON.stringify(value)
    const newFilter = text?.replace(new RegExp(`"${name}"`, 'g'), value)
    return jsonTryParse(newFilter)
  }

  const dict = map(replaceValues, (item) => {
    return { ...item }
  })

  let newFilter = { ...filter }
  forEach(dict, (item) => {
    newFilter = argsReplaceDictionary(newFilter, item?.name, item?.value)
  })
  return newFilter
}

//get the dynamic values on filter
//check if this should be on date filter only
// const filterField = '{{CURRENTMONTH}}'
export function getDynamicDateFilter({ filterString, timeZone = 'America/Chicago' }: { filterString: string; timeZone?: string }) {
  const startOrEnd = (value) => {
    const valueArr = value?.split('OF')
    const result: any = {}
    if (valueArr?.length === 2) {
      result.function = valueArr[0] === 'START' ? 'startOf' : 'endOf'
      result.unit = valueArr[1].toLowerCase()
    }

    return result
  }
  if (isEmpty(filterString)) return ''

  return filterString.replace(/<<(.*?)>>/g, (match) => {
    //split the value
    const fieldName = match.split(/<<|>>/).filter(Boolean)[0]

    //split the value
    let valueArr = fieldName.split(' ') ?? []
    valueArr = compact(valueArr)

    let currentDate = toDate(undefined, timeZone, { keepLocalTime: true }) // momentTz().tz(timeZone)
    const startOrEndOf = startOrEnd(valueArr?.[0])
    if (!isEmpty(startOrEndOf)) {
      currentDate = currentDate?.[startOrEndOf?.function](startOrEndOf?.unit)
    }

    //there are 3 items, value , days, unit
    if (valueArr?.length === 3) {
      currentDate.add(valueArr[1] as DurationInputArg1, valueArr[2] as DurationInputArg2)
    }

    return currentDate?.toISOString()
  })
}

export enum DateTagNameEnum {
  STARTOFYEAR = 'STARTOFYEAR',
  ENDOFYEAR = 'ENDOFYEAR',
  STARTOFMONTH = 'STARTOFMONTH',
  ENDOFMONTH = 'ENDOFMONTH',
  STARTOFWEEK = 'STARTOFWEEK',
  ENDOFWEEK = 'ENDOFWEEK',
  STARTOFDAY = 'STARTOFDAY',
  ENDOFYDAY = 'ENDOFDAY',
  CURRENT = 'CURRENT',
}

export enum DateIsEnum {
  DateIs = 'Date is',
  DateIsBefore = 'Date is before',
  DateIsAfter = 'Date is after',
  DateIsBetween = 'Date is between',
  DateIsBlank = '(blank)',
}
export enum DateNameEnum {
  Today = 'Today',
  Tomorrow = 'Tomorrow',
  Yesterday = 'Yesterday',
  ThisWeek = 'This week',
  LastWeek = 'Last Week',
  NextWeek = 'Next Week',
  ThisMonth = 'This Month',
  LastMonth = 'Last Month',
  NextMonth = 'Next Month',
  ThisYear = 'This Year',
  LastYear = 'Last Year',
  NextYear = 'Next Year',
  ExactDate = 'Exact Date',
}
export type DateRange = { $gte?: string; $lte?: string; $eq?: string }
export const DateNameValue: Record<DateNameEnum, DateRange> = {
  [DateNameEnum.Today]: {
    $gte: `<<${DateTagNameEnum.STARTOFDAY}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYDAY}>>`,
  },
  [DateNameEnum.Tomorrow]: {
    $gte: `<<${DateTagNameEnum.STARTOFDAY} 1 days>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYDAY} 1 days>>`,
  },
  [DateNameEnum.Yesterday]: {
    $gte: `<<${DateTagNameEnum.STARTOFDAY} -1 days>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYDAY} -1 days>>`,
  },
  [DateNameEnum.ThisWeek]: {
    $gte: `<<${DateTagNameEnum.STARTOFWEEK}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFWEEK}>>`,
  },
  [DateNameEnum.LastWeek]: {
    $gte: `<<${DateTagNameEnum.STARTOFWEEK} -1 weeks>>`,
    $lte: `<<${DateTagNameEnum.ENDOFWEEK} -1 weeks>>`,
  },
  [DateNameEnum.NextWeek]: {
    $gte: `<<${DateTagNameEnum.STARTOFWEEK} 1 weeks>>`,
    $lte: `<<${DateTagNameEnum.ENDOFWEEK} 1 weeks>>`,
  },
  [DateNameEnum.ThisMonth]: {
    $gte: `<<${DateTagNameEnum.STARTOFMONTH}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFMONTH}>>`,
  },
  [DateNameEnum.LastMonth]: {
    $gte: `<<${DateTagNameEnum.STARTOFMONTH} -1 months>>`,
    $lte: `<<${DateTagNameEnum.ENDOFMONTH} -1 months>>`,
  },
  [DateNameEnum.NextMonth]: {
    $gte: `<<${DateTagNameEnum.STARTOFMONTH} 1 months>>`,
    $lte: `<<${DateTagNameEnum.ENDOFMONTH} 1 months>>`,
  },
  [DateNameEnum.ThisYear]: {
    $gte: `<<${DateTagNameEnum.STARTOFYEAR}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYEAR}>>`,
  },
  [DateNameEnum.LastYear]: {
    $gte: `<<${DateTagNameEnum.STARTOFYEAR} -1 years>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYEAR} -1 years>>`,
  },
  [DateNameEnum.NextYear]: {
    $gte: `<<${DateTagNameEnum.STARTOFYEAR} 1 years>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYEAR} 1 years>>`,
  },
  [DateNameEnum.ExactDate]: {
    $eq: null,
  },
}

/**
 * Splits an object into two based on the presence of special keys ($or, $nor, $and).
 * @param obj The object to split.
 * @returns An object with two properties: `withOperators` and `withoutOperators`.
 */
export function splitObjectByOperators(obj: Record<string, any>): { withOperators: Record<string, any>; withoutOperators: Record<string, any> } {
  let withOperators: Record<string, any> = {}
  const withoutOperators: Record<string, any> = {}

  // Define the special operators we're interested in
  const operators = ['$or', '$nor', '$and']

  // Iterate through the object's keys
  toPairs(obj).forEach(([key, value]) => {
    // Check if the key is one of the operators or if the value itself is an object that might contain nested operators
    if (
      operators.includes(key) ||
      (typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value).some((subKey) => operators.includes(subKey)))
    ) {
      //const valueKey = first(Object.keys(value))
      withOperators = {
        ...withOperators,
        ...value,
      }
    } else {
      withoutOperators[key] = value
    }
  })

  return { withOperators, withoutOperators }
}
